import { BookingType } from "@/data";
import { toDay } from "@/lib/datetime";
import { parseISO } from "date-fns";
import React, { useState } from "react";
import { CalendarSwitch } from "../calendar_switch";
import { DateTimeValue, isHourlyValue } from "./date_time_picker";
import { ScrollableCalendar } from "./scrollable_calendar";
import { DurationRangePicker } from "./duration_range_picker";
import { TimeSlot } from "./checkout_datetime_picker";
import { getHours } from "@/offering/time_picker";
import { addHours, Time } from "@/offering/time_utils";

interface ScrollableDateTimePickerProps {
  value?: DateTimeValue;
  onChange: (value?: DateTimeValue) => void;
  openAt?: string;
  closedAt?: string;
  timeSlotIncrement?: number;
}

export function ScrollableDateTimePicker({
  value,
  onChange,
  openAt,
  closedAt,
  timeSlotIncrement,
}: ScrollableDateTimePickerProps) {
  const [switchValue, setSwitchValue] = useState(
    isHourlyValue(value) || !value?.startDate
      ? BookingType.Hourly
      : BookingType.Daily
  );

  const startTimeSlots =
    isHourlyValue(value) && value.date
      ? getHours({
          step: timeSlotIncrement,
          startTime: openAt,
          endTime: closedAt,
          formatter: (time: Time) => time,
        })
      : [];
  const timeslots: TimeSlot[] = startTimeSlots.map((slot) => ({
    startTime: slot.label,
    endTime: getHours({
      step: timeSlotIncrement,
      startTime: addHours(slot.value, 1, true),
      endTime: closedAt,
      formatter: (time: Time) => time,
    }).map((s) => s.label),
  }));

  const handleSwitch = (value: BookingType) => {
    setSwitchValue(value);
    onChange(undefined);
  };

  return (
    <div>
      <div className="sticky-top p-3 border-bottom bg-body">
        <CalendarSwitch value={switchValue} onChange={handleSwitch} />
      </div>
      <ScrollableCalendar
        selectionMode={switchValue === BookingType.Hourly ? "single" : "range"}
        selectedDate={
          isHourlyValue(value) && value.date ? parseISO(value.date) : undefined
        }
        selectedRange={
          !isHourlyValue(value) && value?.startDate && value?.endDate
            ? {
                startDate: parseISO(value.startDate),
                endDate: parseISO(value.endDate),
              }
            : undefined
        }
        onSelectDate={(date) =>
          onChange({
            date: toDay(date),
            startTime: undefined,
            endTime: undefined,
          })
        }
        onSelectRange={(range) =>
          onChange({
            startDate: toDay(range.startDate),
            endDate: toDay(range.endDate),
          })
        }
      />
      {switchValue === BookingType.Hourly && (
        <div className="sticky-bottom time-range-picker d-flex justify-content-between pt-3 px-3 bg-body border-top">
          <DurationRangePicker
            onChangeTime={(timeValue) => {
              onChange({
                date: isHourlyValue(value) ? value.date : undefined,
                startTime: timeValue.start,
                endTime: timeValue.end,
              });
            }}
            timeslots={timeslots}
            value={{
              start: isHourlyValue(value) ? value?.startTime : undefined,
              end: isHourlyValue(value) ? value?.endTime : undefined,
            }}
          />
        </div>
      )}
    </div>
  );
}
